import React, { useState, useEffect } from 'react';
import { Project, ProjectMetadata } from '../types';
import { createLocalProjectStructure, setProjectHandle, loadProjectFromLocal } from '../services/fileSystemService';

interface ProjectModalProps {
  projects: Project[];
  onSelect: (projectId: string) => void;
  onCreate: (projectNumber: string, metadata?: ProjectMetadata, id?: string, keepHandle?: boolean) => void;
  onCancel: () => void;
}

type ModalView = 'LIST' | 'CREATE_DETAILS';
type StorageMode = 'BROWSER' | 'LOCAL';

const PRESET_CUSTOMERS = [
  "FrieslandCampina", "Nestlé Global", "Danone Nutricia", "Arla Foods", "Heineken NV", "Standard Bio-Tech"
];

const PRESET_PILOTS = [
  "Senior Inspector van der Berg", "Lead Auditor Janssen", "Field Specialist Smits", "Robotic Operator de Jong"
];

const PRESET_ASSETS = [
  "Spray Dryer Chamber #01", "Storage Tank A-404", "Buffer Vessel V-200", "Crystallization Unit 4", "Main Processing Silo"
];

// Auto-generate a project number based on current date, e.g. PN-2026-0311
const generateProjectNumber = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `PN-${y}-${m}${d}`;
};

export const ProjectModal: React.FC<ProjectModalProps> = ({ projects, onSelect, onCreate, onCancel }) => {
  const [view, setView] = useState<ModalView>('LIST');

  // Project number is auto-generated; user never needs to pick it manually
  const [newProjectNumber] = useState(generateProjectNumber());

  // Screen 3 data
  const [customer, setCustomer] = useState(PRESET_CUSTOMERS[0]);
  const [asset, setAsset] = useState(PRESET_ASSETS[0]);
  const [pilot, setPilot] = useState(PRESET_PILOTS[0]);
  const [storageMode, setStorageMode] = useState<StorageMode>('BROWSER');
  const [isCreatingLocal, setIsCreatingLocal] = useState(false);

  // Load inspector name from login session
  useEffect(() => {
    const savedName = sessionStorage.getItem('nd_inspector_name');
    if (savedName) setPilot(savedName);
  }, []);

  const handleFinalize = async (skipped: boolean = false) => {
    if (newProjectNumber.trim()) {
      const metadata: ProjectMetadata = skipped ? {} : {
        customer: customer.trim(),
        asset: asset.trim(),
        pilot: pilot.trim()
      };

      if (storageMode === 'BROWSER') {
        setProjectHandle(null);
      }

      if (storageMode === 'LOCAL' && !skipped) {
        setIsCreatingLocal(true);
        const success = await createLocalProjectStructure(newProjectNumber.trim(), metadata);
        setIsCreatingLocal(false);
        if (!success) {
          return;
        }
      }

      onCreate(newProjectNumber.trim(), metadata, undefined, storageMode === 'LOCAL');
    }
  };

  const renderContent = () => {
    switch (view) {
      case 'LIST':
        return (
          <div className="space-y-4">
            <label className="text-[10px] text-slate-800 font-bold uppercase tracking-widest ml-1">Existing Project Folders</label>
            <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto custom-scrollbar pr-2">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => onSelect(p.id)}
                  className="flex items-center justify-between p-4 bg-slate-300 border border-slate-400 rounded-none hover:border-[#0000a9]/50 transition-all text-left shadow-sm group"
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-slate-300 group-hover:text-[#0000a9]" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                      <span className="text-[11px] font-black text-slate-900 uppercase tracking-wide group-hover:text-[#0000a9]">{p.projectNumber}</span>
                    </div>
                    {p.metadata?.customer && (
                      <div className="flex items-center gap-2 ml-8">
                        <span className="text-[9px] text-slate-800 font-mono uppercase tracking-widest">{p.metadata.customer}</span>
                        <span className="text-[8px] text-slate-300">|</span>
                        <span className="text-[9px] text-slate-800 font-mono uppercase tracking-widest">{p.metadata.asset}</span>
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-500/20 p-2 border border-slate-500/20 group-hover:bg-[#0000a9] group-hover:text-white transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setView('CREATE_DETAILS')}
                className="w-full py-3 bg-[#0000a9]/10 border border-[#0000a9]/30 text-[#0000a9] hover:bg-[#0000a9]/20 rounded-none text-[10px] font-black uppercase tracking-widest transition-all"
              >
                + Create New Project Folder
              </button>
              <button
                onClick={async () => {
                  const result = await loadProjectFromLocal();
                  if (result) {
                    const { manifest } = result;
                    onCreate(manifest.projectNumber, manifest.metadata, manifest.id, true);
                  }
                }}
                className="w-full py-3 border border-dashed border-slate-500 text-slate-800 hover:text-slate-700 hover:border-slate-400 rounded-none text-[10px] font-bold uppercase tracking-widest transition-all"
              >
                📂 Open Local Project Folder
              </button>
            </div>
          </div>
        );

      case 'CREATE_DETAILS':
        return (
          <div className="space-y-6 animate-slideLeft">
            <div className="space-y-4">
              <label className="text-[10px] text-slate-800 font-black uppercase tracking-widest ml-1">Mission Parameters</label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 text-left">
                  <label className="text-[10px] text-slate-800 font-bold uppercase tracking-widest ml-1">Customer / Org</label>
                  <input
                    list="customer-preset-list"
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                    placeholder="Select or type..."
                    className="w-full bg-slate-300 border border-slate-500 rounded-none p-3 text-slate-900 text-xs focus:border-[#0000a9] outline-none transition shadow-sm placeholder-slate-500 font-black uppercase"
                  />
                  <datalist id="customer-preset-list">
                    {PRESET_CUSTOMERS.map(c => <option key={c} value={c}>{c}</option>)}
                  </datalist>
                </div>
                <div className="space-y-2 text-left">
                  <label className="text-[10px] text-slate-800 font-bold uppercase tracking-widest ml-1">Pilot / Operator</label>
                  <input
                    list="pilot-preset-list"
                    value={pilot}
                    onChange={(e) => setPilot(e.target.value)}
                    placeholder="Select or type..."
                    className="w-full bg-slate-300 border border-slate-500 rounded-none p-3 text-slate-900 text-xs focus:border-[#0000a9] outline-none transition shadow-sm placeholder-slate-500 font-black uppercase"
                  />
                  <datalist id="pilot-preset-list">
                    {PRESET_PILOTS.map(p => <option key={p} value={p}>{p}</option>)}
                  </datalist>
                </div>
              </div>
              <div className="space-y-2 text-left">
                <label className="text-[10px] text-slate-800 font-bold uppercase tracking-widest ml-1">Asset Identifier</label>
                <input
                  list="asset-preset-list"
                  value={asset}
                  onChange={(e) => setAsset(e.target.value)}
                  placeholder="Select or type..."
                  className="w-full bg-slate-300 border border-slate-500 rounded-none p-3 text-slate-900 text-xs focus:border-[#0000a9] outline-none transition shadow-sm placeholder-slate-500 font-black uppercase"
                />
                <datalist id="asset-preset-list">
                  {PRESET_ASSETS.map(a => <option key={a} value={a}>{a}</option>)}
                </datalist>
              </div>

              <div className="space-y-2 pt-2 text-left">
                <label className="text-[10px] text-slate-800 font-bold uppercase tracking-widest ml-1">Storage Target</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStorageMode('BROWSER')}
                    className={`flex-1 p-4 border text-[10px] font-black tracking-widest rounded-none transition shadow-sm ${storageMode === 'BROWSER' ? 'bg-[#0000a9] border-[#0000a9] text-white' : 'bg-slate-300 border-slate-400 text-slate-700'}`}
                  >
                    BROWSER_ONLY
                  </button>
                  <button
                    type="button"
                    onClick={() => setStorageMode('LOCAL')}
                    className={`flex-1 p-4 border text-[10px] font-black tracking-widest rounded-none transition shadow-sm ${storageMode === 'LOCAL' ? 'bg-[#0000a9] border-[#0000a9] text-white' : 'bg-slate-300 border-slate-400 text-slate-700'}`}
                  >
                    FS_LOCAL_MAP
                  </button>
                </div>
                {storageMode === 'LOCAL' && (
                  <p className="text-[9px] text-slate-800 font-mono text-center">
                    ! Requires directory write permission.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => handleFinalize(true)}
                className="flex-1 py-5 bg-slate-300 border border-slate-500 hover:bg-slate-400 text-slate-700 font-black uppercase tracking-[0.1em] text-[10px] transition rounded-none shadow-sm"
              >
                Skip Metadata
              </button>
              <button
                type="button"
                onClick={() => handleFinalize(false)}
                disabled={isCreatingLocal}
                className="flex-[2] py-5 bg-[#0000a9] hover:bg-[#0000c0] text-white font-black uppercase tracking-[0.2em] text-[11px] transition shadow-xl rounded-none flex items-center justify-center gap-3"
              >
                {isCreatingLocal ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    CREATING STRUCTURE...
                  </>
                ) : 'INITIALIZE PRODUCTION FOLDER'}
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[400] bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-6 animate-fadeIn">
      <div className="bg-slate-400 border border-slate-500 w-full max-w-md rounded-none shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-500/30 bg-slate-500/20 flex justify-between items-center">
          <div className="text-left">
            <h3 className="text-slate-900 font-black uppercase tracking-widest text-sm">
              {view === 'CREATE_DETAILS' ? 'Mission Parameters' : 'Storage Configuration'}
            </h3>
            <p className="text-[10px] text-slate-800 font-mono mt-1 uppercase">
              {view === 'CREATE_DETAILS' ? 'Define operational metadata' : 'Define Inspection Destination Folder'}
            </p>
          </div>
          <button onClick={onCancel} aria-label="Close modal" className="text-slate-800 hover:text-slate-700 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
